import React, { useEffect, useRef, useState } from "react";

const defaultRequest = (url, options = {}) =>
  fetch(url, {
    credentials: "include",
    ...options,
  });

const sanitizePresetPayload = (payload) => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(payload).filter(([key]) => !/^entry_usdt_\d+$/.test(key))
  );
};

export default function AdminSharedPresetPanel({
  isAdminAccess,
  featureKey,
  buildPresetPayload,
  applyPreset,
  request,
  reloadKey = "",
  /** true: 라벨·이름·선택·저장·삭제를 한 줄(가로 스크롤)로 */
  singleRow = false,
}) {
  const [presetNameInput, setPresetNameInput] = useState("");
  const [presetNames, setPresetNames] = useState([]);
  const [selectedPresetName, setSelectedPresetName] = useState("");
  const requestRef = useRef(request || defaultRequest);

  useEffect(() => {
    requestRef.current = request || defaultRequest;
  }, [request]);

  const loadPresetNames = async () => {
    const response = await requestRef.current(`/admin/presets?feature=${encodeURIComponent(featureKey)}`);
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data?.message || "프리셋 목록을 불러오지 못했습니다.");
    }

    const nextNames = Array.isArray(data?.presets)
      ? data.presets
          .map((item) => String(item?.name || "").trim())
          .filter(Boolean)
      : [];

    setPresetNames(nextNames);
    setSelectedPresetName((prev) => (nextNames.includes(prev) ? prev : ""));
  };

  useEffect(() => {
    if (!isAdminAccess) {
      setPresetNameInput("");
      setPresetNames([]);
      setSelectedPresetName("");
      return;
    }

    loadPresetNames().catch((error) => {
      console.error(`${featureKey} preset list error:`, error);
      alert(error?.message || "프리셋 목록을 불러오지 못했습니다.");
    });
  }, [isAdminAccess, featureKey, reloadKey]);

  if (!isAdminAccess) {
    return null;
  }

  const handleSelectPreset = async (name) => {
    const nextName = String(name || "").trim();
    setSelectedPresetName(nextName);
    setPresetNameInput(nextName);

    if (!nextName) {
      return;
    }

    try {
      const response = await requestRef.current(
        `/admin/preset?feature=${encodeURIComponent(featureKey)}&name=${encodeURIComponent(nextName)}`
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data?.message || "프리셋을 불러오지 못했습니다.");
      }

      applyPreset(sanitizePresetPayload(data?.payload));
    } catch (error) {
      console.error(`${featureKey} preset load error:`, error);
      alert(error?.message || "프리셋을 불러오지 못했습니다.");
    }
  };

  const handleSavePreset = async () => {
    const name = presetNameInput.trim();
    if (!name) {
      alert("프리셋 이름을 입력해주세요.");
      return;
    }

    const isExistingName = presetNames.includes(name);
    if (isExistingName) {
      const confirmed = window.confirm(`${name} 프리셋이 이미 있습니다. 덮어쓰시겠습니까?`);
      if (!confirmed) {
        return;
      }
    }

    try {
      const payload = sanitizePresetPayload(buildPresetPayload());
      const response = await requestRef.current("/admin/preset", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          feature: featureKey,
          name,
          payload,
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data?.message || "프리셋을 저장하지 못했습니다.");
      }

      await loadPresetNames();
      setSelectedPresetName(name);
      setPresetNameInput(name);
      alert(data?.message || "프리셋이 저장되었습니다.");
    } catch (error) {
      console.error(`${featureKey} preset save error:`, error);
      alert(error?.message || "프리셋을 저장하지 못했습니다.");
    }
  };

  const handleDeletePreset = async () => {
    const name = (selectedPresetName || presetNameInput).trim();
    if (!name) {
      alert("삭제할 프리셋을 선택해주세요.");
      return;
    }

    const confirmed = window.confirm(`${name} 프리셋을 삭제하시겠습니까?`);
    if (!confirmed) {
      return;
    }

    try {
      const response = await requestRef.current(
        `/admin/preset?feature=${encodeURIComponent(featureKey)}&name=${encodeURIComponent(name)}`,
        { method: "DELETE" }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data?.message || "프리셋을 삭제하지 못했습니다.");
      }

      await loadPresetNames();
      setSelectedPresetName("");
      setPresetNameInput("");
      alert(data?.message || "프리셋이 삭제되었습니다.");
    } catch (error) {
      console.error(`${featureKey} preset delete error:`, error);
      alert(error?.message || "프리셋을 삭제하지 못했습니다.");
    }
  };

  const fieldClassName = singleRow ? "admin-shared-preset-panel__field" : undefined;

  const fieldStyle = singleRow
    ? undefined
    : {
        height: "40px",
        padding: "0 12px",
        borderRadius: "10px",
        border: "1px solid rgba(255,255,255,0.16)",
        background: "rgba(7, 10, 18, 0.88)",
        color: "#f7fbff",
        flex: "1 1 180px",
        minWidth: "180px",
      };

  const actionsClassName = singleRow
    ? "admin-shared-preset-panel__actions"
    : "kp-symbol-controls__buttons";

  return (
    <section
      className={`kp-card kp-card--controls${singleRow ? " admin-shared-preset-panel--single-row" : ""}`}
      style={{
        marginTop: "-20px",
        marginBottom: "-20px",
        padding: "12px 20px",
      }}
    >
      <div
        className={singleRow ? "admin-shared-preset-panel__row" : undefined}
        style={{
          display: "flex",
          flexWrap: singleRow ? "nowrap" : "wrap",
          gap: singleRow ? "10px" : "8px",
          alignItems: "center",
          ...(singleRow ? { overflowX: "auto", width: "100%", WebkitOverflowScrolling: "touch" } : {}),
        }}
      >
        <strong
          className={singleRow ? "admin-shared-preset-panel__title" : undefined}
          style={
            singleRow
              ? undefined
              : {
                  color: "#ffd18a",
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                  minWidth: "110px",
                }
          }
        >
          관리자 공용 프리셋
        </strong>
        <input
          type="text"
          className={fieldClassName}
          value={presetNameInput}
          onChange={(event) => setPresetNameInput(event.target.value)}
          placeholder="프리셋 이름"
          style={fieldStyle}
        />
        <select
          className={fieldClassName}
          value={selectedPresetName}
          onChange={(event) => handleSelectPreset(event.target.value)}
          style={fieldStyle}
        >
          <option value="" style={{ color: "#f7fbff", backgroundColor: "#070a12" }}>
            프리셋 선택
          </option>
          {presetNames.map((name) => (
            <option
              key={name}
              value={name}
              style={{ color: "#f7fbff", backgroundColor: "#070a12" }}
            >
              {name}
            </option>
          ))}
        </select>
        <div
          className={actionsClassName}
          style={{ height: "40px", gap: "8px", flexShrink: 0, display: "flex", alignItems: "center" }}
        >
          <button
            type="button"
            className="button_benefit_c_ut"
            onClick={handleSavePreset}
            style={{ height: "40px", minHeight: "40px", padding: "0 16px", marginBottom: 0, fontSize: "13px" }}
          >
            저장
          </button>
          <button
            type="button"
            className="button_benefit_c_ut"
            onClick={handleDeletePreset}
            style={{ height: "40px", minHeight: "40px", padding: "0 16px", marginBottom: 0, fontSize: "13px" }}
          >
            삭제
          </button>
        </div>
      </div>
    </section>
  );
}
